import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  sortModGroups,
  getNestedModGroupsForSelectedModGroup,
} from '../utils/menu';
import { CartItem, CartModifierGroup } from '../utils/cart';
import {
  ParsedMenuItem,
  ParsedModifierGroup,
  SlashCommand,
} from '../types/menu';
import { selectRestaurant, selectStage } from './restaurantSlice';
import { selectMenuVersion } from './menuSlice';
import { orderActions } from './orderSlice';
import { sendHITLSessionEnd } from './messagingSlice';
import { SLASH_COMMAND_REMINDERS } from '../utils/constants';

// Navigation level constants
export const NAVIGATION_LEVELS = {
  TOP: 'top',
  NESTED: 'nested',
} as const;

export type NavigationLevel =
  (typeof NAVIGATION_LEVELS)[keyof typeof NAVIGATION_LEVELS];

export interface DialogState {
  step: number;
  steps: string[];
  slashCommands: SlashCommand[];
  slashCommandIndex: number;
  selectedItem: CartItem | undefined;
  selectedItemCartId: number;
  invalidModGroupDescriptions: string[];
  voiceBoardValue: string;
  intentsStatus: Intent[];
  nextSelectedModGroupIndex: number;
  selectedModGroup?: ParsedModifierGroup;
  selectedModGroupPath?: string; // Unique path to the selected modifier group
  highlightedModifier?: string; // Highlighted in Omnibar search result
  clickedModifier?: string; // Path to the clicked modifier for visual feedback
  // Navigation context for hierarchical modifier group navigation
  navigationLevel: NavigationLevel;
  parentModGroupId?: string; // ID of the parent modifier group when in nested level
}

interface Intent {
  clickable: boolean;
  lastMessageIndex: number | undefined;
}
const initialState: DialogState = {
  step: 0,
  selectedItem: undefined,
  selectedItemCartId: 0,
  invalidModGroupDescriptions: [],
  voiceBoardValue: '',
  steps: ['What can I get for you today?', 'Sure thing.'],
  slashCommands: [],
  slashCommandIndex: 0,
  intentsStatus: [],
  nextSelectedModGroupIndex: 0,
  selectedModGroupPath: undefined,
  clickedModifier: undefined,
  navigationLevel: NAVIGATION_LEVELS.TOP,
  parentModGroupId: undefined,
};
const dialogSlice = createSlice({
  name: 'dialog',
  initialState: initialState,
  reducers: {
    updateSelectedItem: (
      state,
      action: PayloadAction<{ item: CartItem; itemCartId: number } | undefined>
    ) => {
      if (action.payload) {
        state.selectedItem = action.payload.item;
        state.selectedItemCartId = action.payload.itemCartId;
      } else {
        state.selectedItem = undefined;
        state.selectedItemCartId = 0;
      }
    },
    cloneSelectedItem: (
      state,
      action: PayloadAction<{ cartItemId: number }>
    ) => {
      state.selectedItemCartId = action.payload.cartItemId;
      state.nextSelectedModGroupIndex = 0;
      state.selectedModGroup = undefined;
      state.selectedModGroupPath = undefined;
      state.clickedModifier = undefined;
      state.navigationLevel = NAVIGATION_LEVELS.TOP;
      state.parentModGroupId = undefined;
    },
    resetDialogSelectedItem: (state) => {
      state.selectedItem = undefined;
      state.selectedItemCartId = 0;
    },
    selectItemAndResetNavigation: (
      state,
      action: PayloadAction<{ item: CartItem; itemCartId: number } | undefined>
    ) => {
      // Update selected item
      if (action.payload) {
        state.selectedItem = action.payload.item;
        state.selectedItemCartId = action.payload.itemCartId;
      } else {
        state.selectedItem = undefined;
        state.selectedItemCartId = 0;
      }

      // Reset all navigation-related state
      state.nextSelectedModGroupIndex = 0;
      state.selectedModGroup = undefined;
      state.selectedModGroupPath = undefined;
      state.clickedModifier = undefined;
      state.navigationLevel = NAVIGATION_LEVELS.TOP;
      state.parentModGroupId = undefined;
    },
    addInvalidModGroupDescriptions: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.invalidModGroupDescriptions.concat(action.payload);
    },
    increaseStep: (state) => {
      state.step += 1;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setSteps: (state, action: PayloadAction<string[]>) => {
      state.steps = action.payload;
    },
    setVoiceBoardValue: (state, action: PayloadAction<string>) => {
      state.voiceBoardValue = action.payload;
    },
    initIntentsStatus: (state, action: PayloadAction<Intent[]>) => {
      state.intentsStatus = action.payload;
    },
    setIntentClickable: (
      state,
      action: PayloadAction<{ idx: number; status: boolean }>
    ) => {
      state.intentsStatus[action.payload.idx].clickable = action.payload.status;
    },
    setIntentLastMessageIdx: (
      state,
      action: PayloadAction<{ idx: number; lastMessageIndex: number }>
    ) => {
      state.intentsStatus[action.payload.idx].lastMessageIndex =
        action.payload.lastMessageIndex;
    },
    updateSelectedModGroup: (
      state,
      action: PayloadAction<{
        modGroup: ParsedModifierGroup | CartModifierGroup | undefined;
        pathToItem?: string;
      }>
    ) => {
      const { modGroup: selectedModGroup, pathToItem } = action.payload;
      if (selectedModGroup && state.selectedItem) {
        state.selectedModGroup = selectedModGroup;
        state.selectedModGroupPath = pathToItem;

        let modGroupsToSearch: ParsedModifierGroup[] = [];

        if (state.navigationLevel === NAVIGATION_LEVELS.TOP) {
          // Search in top-level modifier groups
          modGroupsToSearch = sortModGroups(state.selectedItem);
        } else if (
          state.navigationLevel === NAVIGATION_LEVELS.NESTED &&
          state.parentModGroupId
        ) {
          // Search in nested modifier groups for the current parent
          modGroupsToSearch = getNestedModGroupsForSelectedModGroup(
            state.selectedItem,
            state.parentModGroupId
          );
        }

        const currentSelectedModGroupIndex = modGroupsToSearch.findIndex(
          (modGroup) => modGroup.id === selectedModGroup.id
        );
        state.nextSelectedModGroupIndex =
          currentSelectedModGroupIndex > -1
            ? currentSelectedModGroupIndex + 1
            : 0;
      } else {
        state.selectedModGroup = undefined;
        state.selectedModGroupPath = undefined;
        state.nextSelectedModGroupIndex = 0;
      }
    },
    setSelectedModGroupIndex: (state, action: PayloadAction<number>) => {
      state.nextSelectedModGroupIndex = action.payload;
    },
    setHighlightedModifier: (state, action: PayloadAction<string>) => {
      state.highlightedModifier = action.payload;
    },
    addDialogStep: (
      state,
      action: PayloadAction<{ step: string; index?: number }>
    ) => {
      const { step, index } = action.payload;
      const insertIndex = index !== undefined ? index : state.step;
      state.steps.splice(insertIndex, 0, step);
    },
    addSlashCommands: (state, action: PayloadAction<SlashCommand[]>) => {
      state.slashCommands = action.payload;
      state.slashCommandIndex = 0; // Reset index when new slash commands are added
    },
    nextSlashCommand: (state) => {
      if (state.slashCommandIndex < state.slashCommands.length - 1) {
        state.slashCommandIndex += 1;
      } else {
        // When at the last slash command, advance beyond the array to trigger fallback
        state.slashCommandIndex = state.slashCommands.length;
      }
    },
    // HITL-1429: drop one queued slash command because it has already been
    // fulfilled somewhere other than the queue — specifically, the agent
    // accepted the AI's suggestion for the same upsell, which spoke the prompt
    // and added the item. Left in the queue it would come round again on Enter
    // and the customer would hear the same upsell twice (the HITL-1540
    // symptom).
    //
    // Removes the entry rather than advancing past it (unlike nextSlashCommand
    // / skipUpsellInQueue): the fulfilled command need not be the one the cursor
    // is on, so advancing would consume an unrelated, still-pending prompt.
    // No-op when the intent is not queued, which is the common case — the AI can
    // offer an upsell the reminder queue never seeded.
    removeSlashCommandByIntent: (state, action: PayloadAction<string>) => {
      const removeAt = state.slashCommands.findIndex(
        (slashCommand) => slashCommand.intent === action.payload
      );
      if (removeAt === -1) {
        return;
      }
      state.slashCommands.splice(removeAt, 1);
      // An entry removed BEFORE the cursor shifts everything after it down by
      // one, so the cursor follows to keep pointing at the same command. One
      // removed AT the cursor needs no adjustment — the next command has taken
      // its index. Clamp so the cursor never sits further past the end than the
      // fallback position nextSlashCommand parks it at.
      if (removeAt < state.slashCommandIndex) {
        state.slashCommandIndex -= 1;
      }
      if (state.slashCommandIndex > state.slashCommands.length) {
        state.slashCommandIndex = state.slashCommands.length;
      }
    },
    // HITL-1253: skip any upsell_end_of_order entries the queue is currently
    // pointing at by advancing slashCommandIndex past them. Used by readback
    // so the upsell prompt won't fire next via slash command. Leaves the
    // queue array untouched and is a no-op if the index is already past
    // (or before but not at) an upsell entry.
    skipUpsellInQueue: (state) => {
      while (
        state.slashCommandIndex < state.slashCommands.length &&
        state.slashCommands[state.slashCommandIndex].intent.startsWith(
          SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
        )
      ) {
        state.slashCommandIndex += 1;
      }
    },
    // Navigate deeper into nested modifier groups
    navigateToNestedLevel: (
      state,
      action: PayloadAction<{
        parentModGroupId: string;
        firstNestedModGroup?: ParsedModifierGroup;
        firstNestedModGroupPath?: string;
      }>
    ) => {
      const { firstNestedModGroupPath, firstNestedModGroup, parentModGroupId } =
        action.payload || {};
      state.navigationLevel = NAVIGATION_LEVELS.NESTED;
      state.parentModGroupId = parentModGroupId;
      state.nextSelectedModGroupIndex = 1; // Set to 1 since we're selecting the first item
      state.selectedModGroup = firstNestedModGroup;
      state.selectedModGroupPath = firstNestedModGroupPath;
    },
    // Navigate back to parent level
    navigateToParentLevel: (
      state,
      action: PayloadAction<{
        parentModGroup?: ParsedModifierGroup;
        parentModGroupPath?: string;
      }>
    ) => {
      const { parentModGroup, parentModGroupPath } = action.payload || {};
      state.navigationLevel = NAVIGATION_LEVELS.TOP;
      state.parentModGroupId = undefined;

      if (parentModGroup && state.selectedItem) {
        // Find the index of the parent modifier group in top-level groups
        const topLevelModGroups = sortModGroups(state.selectedItem);
        const parentIndex = topLevelModGroups.findIndex(
          (modGroup) => modGroup.id === parentModGroup.id
        );

        state.nextSelectedModGroupIndex =
          parentIndex > -1 ? parentIndex + 1 : 0;
        state.selectedModGroup = parentModGroup;
        state.selectedModGroupPath = parentModGroupPath;
      } else {
        state.nextSelectedModGroupIndex = 0;
        state.selectedModGroup = undefined;
        state.selectedModGroupPath = undefined;
      }
    },
    setClickedModifier: (state, action: PayloadAction<string | undefined>) => {
      state.clickedModifier = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Reset slash commands state on HITL session end
    builder.addCase(sendHITLSessionEnd, (state) => {
      state.slashCommands = [];
      state.slashCommandIndex = 0;
    });
    builder.addMatcher(
      isAnyOf(
        selectRestaurant.fulfilled,
        selectStage.fulfilled,
        selectMenuVersion.fulfilled
      ),
      () => {
        return initialState;
      }
    );
  },
});

export const dialogActions = dialogSlice.actions;
export const {
  resetDialogSelectedItem,
  selectItemAndResetNavigation,
  addDialogStep,
  setSteps,
  addSlashCommands,
  nextSlashCommand,
  skipUpsellInQueue,
  removeSlashCommandByIntent,
} = dialogActions;
export default dialogSlice.reducer;
